import { LegalPage, LegalSection } from "./LegalPage";

const enforcementControls = [
  {
    slug: "automated-moderation",
    title: "Automated content moderation",
    body: "Profiles containing language related to hookups, solicitation, or dishonest relationship status are automatically flagged and removed.",
  },
  {
    slug: "photo-verification",
    title: "Photo verification",
    body: "Members can earn a verified badge through selfie-based identity verification. All profile photos are subject to admin review and may be rejected if they violate community standards.",
  },
  {
    slug: "user-reporting",
    title: "User reporting",
    body: "Any member can report another member with a category selection and a free-text description. Every report is reviewed by our admins.",
  },
  {
    slug: "blocking",
    title: "Blocking",
    body: "Members can block any other member, permanently removing them from their experience.",
  },
  {
    slug: "suspension-removal",
    title: "Account suspension and removal",
    body: "Community Hearts reserves the right to suspend or permanently ban any account found in violation of this policy.",
  },
  {
    slug: "inactivity-deletion",
    title: "Inactivity auto-deletion",
    body: "Accounts with no activity and no active connections after 5 days of signup are automatically removed.",
  },
  {
    slug: "age-requirement",
    title: "Age requirement",
    body: "Members must be 18 or older. Providing false age information is a violation and grounds for immediate removal.",
  },
  {
    slug: "zero-tolerance",
    title: "Zero tolerance for explicit content",
    body: "Profiles containing explicit photos, sexual solicitation, or harassment are removed immediately.",
  },
  {
    slug: "law-enforcement",
    title: "Law enforcement cooperation",
    body: "Community Hearts will cooperate with valid legal requests and law enforcement inquiries in accordance with applicable law.",
  },
];

const AcceptableUsePage = () => (
  <LegalPage
    title="Acceptable Use Policy"
    updated="Last updated August 2026"
    testId="aup-page"
    lede="The standard of the room. Courtesy is not optional."
  >
    <LegalSection title="The short version">
      <p>Community Hearts exists for one reason: real, genuine connections between people who mean it. This policy explains what that means in practice. It applies to this waitlist page today, and to the app when it launches. If someone is here to play games, they're not here for long.</p>
    </LegalSection>
    <LegalSection title="Who may use Community Hearts">
      <p>You must be at least 18 years old. You join as yourself — one account per person, with your real identity and real photos of you. Identity verification is the price of entry, not an upgrade.</p>
    </LegalSection>
    <LegalSection title="Respect and safety">
      <p>Community Hearts is built for the LGBTQ+ community and its allies, and everyone here deserves to feel safe. You may not harass, threaten, shame, or target anyone — including hate speech, slurs, or discrimination of any kind. Never out another member or share their profile, photos, or messages outside the app without their consent. What happens in the community stays in the community.</p>
    </LegalSection>
    <LegalSection title="Honesty">
      <p>No catfishing, impersonation, or fake profiles. No stolen or AI-generated photos presented as yourself. No misrepresenting your age, identity, or intentions. Profiles that fail verification are removed.</p>
    </LegalSection>
    <LegalSection title="No scams or solicitation">
      <p>Never ask other members for money, gifts, gift cards, investments, or financial favors — for any reason, including emergencies. Romance scams get an immediate, permanent ban. Don't use the app to advertise, sell, recruit, or promote outside services, and don't use matches as a sales funnel.</p>
    </LegalSection>
    <LegalSection title="Content standards">
      <p>No nudity or sexually explicit content in profiles, photos, voice notes, or messages. No illegal content, no content involving minors in any context, and nothing that violates another person's rights. Keep first messages respectful — unsolicited explicit messages are a fast track out.</p>
      <p>Profiles that use certain words, including OnlyFans, hookup, couple or couples, married, looking for fun, sex, or nudes, are hidden from Discover and flagged for review. The account is not deleted automatically.</p>
    </LegalSection>
    <LegalSection title="Events">
      <p>Events submitted to Community Hearts must be real, lawful, and welcoming to LGBTQ+ members. Hosts are responsible for their own events, venues, and attendees. Events created to sell, solicit, or discriminate will be removed and may cost you your account.</p>
    </LegalSection>
    <LegalSection title="Platform integrity">
      <p>No bots, scrapers, or automated tools. No gaming the referral system with fake accounts or self-referrals. No attempting to access administrative areas or other members' data. No interfering with anyone else's signup or experience.</p>
    </LegalSection>
    <LegalSection title="Enforcement">
      <p>Every report is reviewed by a human. Depending on severity, we may warn, suspend, or permanently ban an account — serious violations (scams, hate, safety threats) skip the warning. Verified identity makes bans meaningful: banned means banned. If you believe we made a mistake, reach out through the contact page and a person will take another look.</p>
    </LegalSection>
    <LegalSection title="Enforcement controls">
      <p>These are the tools and automated safeguards we use to keep Community Hearts safe and honest. They apply to the app when it launches.</p>
      <ul className="mt-4 space-y-5" data-testid="aup-enforcement-controls">
        {enforcementControls.map((control) => (
          <li key={control.title} className="border-l-2 border-gold/40 pl-5" data-testid={`aup-control-${control.slug}`}>
            <span className="block font-serif text-lg text-gold">{control.title}</span>
            <span className="mt-1 block text-base leading-relaxed text-ink/70">{control.body}</span>
          </li>
        ))}
      </ul>
    </LegalSection>
    <LegalSection title="Reporting">
      <p>See something that breaks these rules? Report it in the app, or write to us through the contact page. Reports are confidential, and retaliation against reporters is itself a violation.</p>
    </LegalSection>
    <LegalSection title="Changes and contact">
      <p>We may update this policy as we approach launch; the date at the top shows the latest version. Questions about what's allowed? Reach us through the contact page.</p>
    </LegalSection>
  </LegalPage>
);

export default AcceptableUsePage;
